import { CheckinLocationDetails } from './event.type'
import {
  InvitationEmailTemplateButtons,
  InvitationEmailTemplateFontSize,
  InvitationEmailTemplateInfo,
  InvitationEmailTemplateTiming,
  InvitationEmailTemplateTimingDay
} from './invitation-email-template.type'

/**
 * Defaults and helpers for the invitation email custom-template editor.
 */
export class InvitationEmailTemplateModel {
  public static readonly DEFAULT_CARD_BACKGROUND = '#5b6271'
  public static readonly DEFAULT_CALENDER_BUTTON = '#1C64F2'
  public static readonly DEFAULT_FONT_FAMILY = 'Arial, Helvetica, sans-serif'
  public static readonly SUBJECT_MAX_LENGTH = 127

  public static readonly DEFAULT_BUTTONS: InvitationEmailTemplateButtons = {
    confirm: {
      text: 'Confirm',
      textColor: '#ffffff',
      backgroundColor: '#15803d',
      isBold: false,
      isItalic: false,
      isUnderline: false,
      fontSize: '16px'
    },
    decline: {
      text: 'Decline',
      textColor: '#ffffff',
      backgroundColor: '#dc2626',
      isBold: false,
      isItalic: false,
      isUnderline: false,
      fontSize: '16px',
      hidden: false
    }
  }

  /**
   * Builds a full invitation email template info from shared defaults plus overrides.
   */
  public static create(overrides: Partial<InvitationEmailTemplateInfo> = {}): InvitationEmailTemplateInfo {
    const { customButtons: overrideButtons, ...restOverrides } = overrides

    return {
      message: '',
      eventSlug: '',
      formSlug: '',
      cardBackgroundColor: InvitationEmailTemplateModel.DEFAULT_CARD_BACKGROUND,
      calendarButtonColor: InvitationEmailTemplateModel.DEFAULT_CALENDER_BUTTON,
      fontFamily: InvitationEmailTemplateModel.DEFAULT_FONT_FAMILY,
      customButtons: overrideButtons ?? InvitationEmailTemplateModel.cloneButtons(InvitationEmailTemplateModel.DEFAULT_BUTTONS),
      ...restOverrides
    }
  }

  /**
   * Default email subject shown when the template has no saved subject.
   * @returns `{event name} Invitation`, or empty when the event has no name.
   */
  public static buildDefaultSubject(eventName: string | null | undefined): string {
    const name = eventName?.trim()
    return name ? `${name} invitation` : ''
  }

  /**
   * Maps planning-api event timing rows into the custom-template preview shape
   * (day + HH:mm start/end), matching legacy invite template behavior.
   */
  public static mapEventTimings(
    timings:
      | Array<{
          start_date?: string
          info?: string | null
          slots?: Array<{ from?: string; to?: string }> | null
        }>
      | null
      | undefined
  ): InvitationEmailTemplateTiming[] {
    if (!timings?.length) return []

    return timings.flatMap((timing) => {
      const slots = timing.slots?.length ? timing.slots : [{ from: undefined, to: undefined }]

      return slots.map((slot) => ({
        day: timing.start_date ?? null,
        start_datetime: slot.from ?? null,
        end_datetime: slot.to ?? null,
        info: timing.info || null
      }))
    })
  }

  /**
   * Groups flat invitation timings by day for the custom-template preview layout.
   */
  public static groupTimingsByDay(timings: InvitationEmailTemplateTiming[] | null | undefined): InvitationEmailTemplateTimingDay[] {
    if (!timings?.length) return []

    const groups = new Map<string, InvitationEmailTemplateTimingDay>()

    for (const timing of timings) {
      const dayKey = typeof timing.day === 'string' ? timing.day.slice(0, 10) : timing.day instanceof Date ? timing.day.toISOString().slice(0, 10) : `row-${groups.size}`

      let group = groups.get(dayKey)
      if (!group) {
        group = { day: timing.day, slots: [], info: timing.info }
        groups.set(dayKey, group)
      } else if (!group.info && timing.info) {
        group.info = timing.info
      }

      if (timing.start_datetime || timing.end_datetime) {
        group.slots.push({
          from: timing.start_datetime,
          to: timing.end_datetime
        })
      }
    }

    return Array.from(groups.values())
  }

  /**
   * Builds a Google Static Maps URL from invitation-template location details.
   * Returns empty string when coordinates are missing (same behavior as legacy templates).
   */
  public static buildStaticMapUrl(location: CheckinLocationDetails | null | undefined, googleMapsApiKey: string): string {
    if (!location || !googleMapsApiKey) return ''

    const lat = Number(location.map_latitude ?? location.pin_latitude ?? location.venue_latitude) || 0
    const lng = Number(location.map_longitude ?? location.pin_longitude ?? location.venue_longitude) || 0

    if (!lat || !lng || Number.isNaN(lat) || Number.isNaN(lng)) return ''

    const zoom = Number(location.map_zoom_level) || 15
    const width = Math.min(Number(location.map_size_width) || 600, 1280)
    const height = Math.min(Number(location.map_size_height) || 300, 395)
    const marker = encodeURIComponent(`color:red|${lat},${lng}`)

    return `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=${zoom}&size=${width}x${height}&scale=2&maptype=roadmap&markers=${marker}&key=${googleMapsApiKey}`
  }

  /**
   * Converts font-size values to a CSS pixel token (`14px` | `16px` | `18px`).
   */
  public static toFontSizeCss(fontSize: string | number | null | undefined): InvitationEmailTemplateFontSize {
    if (fontSize === undefined || fontSize === null || fontSize === '') return '16px'

    const numericPart = String(fontSize).trim().replace(/px$/i, '').trim()
    if (numericPart === '14') return '14px'
    if (numericPart === '16') return '16px'
    if (numericPart === '18') return '18px'
    return '16px'
  }

  private static cloneButtons(buttons: InvitationEmailTemplateButtons): InvitationEmailTemplateButtons {
    return {
      confirm: { ...buttons.confirm },
      decline: { ...buttons.decline }
    }
  }
}
